import { useEffect, useMemo, useState } from 'react';

import API from '../APIPublic';
import { Button, Field, Groupper, Header, Input, Message } from 'react-frontier'
import { Dropdown } from 'semantic-ui-react';
import { SetLoading } from '@cocoan/components/Classes';
import { InvoiceForm, InvoiceResult } from '../ClassesPublic';
import { bindFormChange } from '../UtilPublic';
import { Link, useSearchParams } from 'react-router-dom';
import { useTitle } from '../HooksPublic';
import { getCFDIList, REGIMENES_FISCALES } from '@cocoan/components/Util';
import Validator from '@cocoan/components/Validator';


const MEXICO_STATES = [
	'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 
	'Coahuila', 'Colima', 'Chiapas', 'Chihuahua', 'Ciudad de México', 'Durango', 'Guanajuato', 
	'Guerrero', 'Hidalgo', 'Jalisco', 'México', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 
	'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 
	'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

var InvoiceCreate = ()=>{
	var { setTitle } = useTitle();
	var [params] = useSearchParams();
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [invoiceResponse, setInvoiceResponse] = useState<InvoiceResult>(null);
	var [form, setForm] = useState<InvoiceForm>({
		order_hash: '',
		email: '',
		legal_name: '',
		rfc: '',
		regimen: '',
		use_cfdi: '',
		address: {
			street: '',
			exterior_number: '',
			interior_number: '',
			neighborhood: '',
			zipcode: '',
			city: '',
			state: '',
			country: 'México'
		}
	});

	useEffect(()=>{
		if(params && params.get('o')){
			setForm({
				...form,
				order_hash: params.get('o'),
			});
		}
		setTitle('Facturación')
	}, [params]);

	var cfdi_list = useMemo(()=>{
		return getCFDIList(form?.regimen ? parseInt(form?.regimen) : null);
	}, [form?.regimen]);

	var createInvoice = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({
			...form,
			...form.address
		}, {
			order_hash: [
				{ rule: /[0-9a-z]{4}/gi, prompt: 'El folio de compra no es válido' }
			],
			email: [
				{ rule: 'email', prompt: 'El correo electrónico no es válido' }
			],
			legal_name: [
				{ rule: 'minLength', params: [3], prompt: 'La razón social no es válida' },
			],
			rfc: [
				{ rule: 'minLength', params: [12], prompt: 'El RFC no es válido' },
			],
			regimen: [
				{ rule: 'empty', prompt: 'Favor de seleccionar el régimen fiscal' }
			],
			use_cfdi: [
				{ rule: 'empty', prompt: 'Favor de seleccionar el uso de CFDI' }
			],
			street: [
				{ rule: 'minLength', params: [3], label: 'Calle' },
			],
			exterior_number: [
				{ rule: 'empty', label: 'Número exterior' },
				{ rule: 'maxLength', params: [16], label: 'Número exterior' },
			],
			interior_number: [
				{ rule: 'maxLength', params: [16], label: 'Número interior' },
			],
			neighborhood: [
				{ rule: 'minLength', params: [3], label: 'Colonia' },
				{ rule: 'maxLength', params: [128], label: 'Colonia' },
			],
			zipcode: [
				{ rule: 'minLength', params: [4], label: 'Código postal' },
				{ rule: 'maxLength', params: [16], label: 'Código postal' },
			],
			city: [
				{ rule: 'minLength', params: [3], label: 'Ciudad' },
				{ rule: 'maxLength', params: [128], label: 'Ciudad' },
			],
			state: [
				{ rule: 'empty', prompt: 'Favor de seleccionar el estado' },
				{ rule: 'maxLength', params: [128], label: 'Estado' },
			]
		});
		setErrorPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.createOrderInvoice(form, form.address).then(res=>{
			if(res.error) return setErrorPrompts([res.message]);
			setInvoiceResponse(res.data);
		}).catch(err=>{
			return setErrorPrompts(['Hubo un error inesperado creando la factura (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var onFormChange = bindFormChange(form, setForm);
	var onAddressChange = bindFormChange(form.address, v=>{
		setForm({
			...form,
			address: v
		});
	})

	return <div className="ar create-invoice screen">
		<Header
			text='Facturar'
			style={{
				margin: '15px auto 20px auto',
				maxWidth: 600,
				padding: '0 10px'
			}}
			subheaderStyle={{
				fontSize: 16,
				lineHeight: '18px'
			}}
		/>
		<Groupper title='Información de compra' width={500} titleCentered actions={(
			<Button text='Facturar' color='black' onClick={createInvoice} disabled={!!invoiceResponse} />
		)}>
			<Input label='Folio de compra' value={form.order_hash} onChange={onFormChange('order_hash')} />
			<Input label='Correo electrónico' value={form.email} onChange={onFormChange('email')} />
			<Groupper.Divider text='Información fiscal' centered style={{ marginTop: 20 }} />
			<Input label='Razón social' value={form.legal_name} onChange={onFormChange('legal_name')} />
			<Input label='RFC' value={form.rfc} onChange={onFormChange('rfc')} />
			<Field label='Régimen fiscal'>
				<Dropdown
					search
					selection
					selectOnBlur={false}
					placeholder='Régimen fiscal'
					value={form.regimen}
					onChange={onFormChange('regimen', true)}
					options={REGIMENES_FISCALES}
				/>
			</Field>
			<Field label='Uso de CFDI'>
				<Dropdown
					search
					selection
					selectOnBlur={false}
					placeholder='Uso de CFDI'
					value={form.use_cfdi}
					onChange={onFormChange('use_cfdi', true)}
					options={cfdi_list}
				/>
			</Field>
			<Groupper.Divider text='Dirección fiscal' centered style={{ marginTop: 20 }} />
			<Input label='Calle' value={form.address.street} onChange={onAddressChange('street')} />
			<Field amount={2}>
				<Input label='Número exterior' value={form.address.exterior_number} onChange={onAddressChange('exterior_number')} />
				<Input label='Número interior' value={form.address.interior_number} onChange={onAddressChange('interior_number')} />
			</Field>
			<Field amount={2}>
				<Input label='Colonia' value={form.address.neighborhood} onChange={onAddressChange('neighborhood')} />
				<Input label='Código postal' value={form.address.zipcode} onChange={onAddressChange('zipcode')} />
			</Field>
			<Input label='Ciudad' value={form.address.city} onChange={onAddressChange('city')} />
			<Field label='Estado'>
				<Dropdown
					search
					selection
					selectOnBlur={false}
					placeholder='Estado'
					value={form.address.state}
					onChange={onAddressChange('state', true)}
					options={MEXICO_STATES.map(a=>({
						value: a, text: a
					}))}
				/>
			</Field>
			<Message type='error' list={errorPrompts} />
			{!!invoiceResponse && (
				<Message type='info' centered>
					<div className="header">Factura creada</div>
					{invoiceResponse.created ? (
						'Hemos creado tu factura con la información proporcionada. Hemos enviado un correo electrónico con la factura a la dirección de correo de la orden. Adicionalmente'
					) : (
						'Ya existía una factura creada para esta orden'
					)}
					, podrás descargarla siguiendo el siguiente enlace
					<Button size='big' color='blue' text='Descargar factura' iconName='file-zipper' as={Link} to={invoiceResponse.invoice_url} style={{ marginTop: 15, width: 250 }} />
				</Message>
			)}
		</Groupper>
	</div>
}

export default InvoiceCreate;